import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT, DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR } from '~/constants';

import { StatusBadge } from './deliveryAgencyTableCells';

import type { TypeDeliveryAgencyOrder } from '~/types/delivery-agency.types';

const getDeliveryAgencyOrdersColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (order: TypeDeliveryAgencyOrder) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeDeliveryAgencyOrder>[] = [
    {
      id: 'rowNumber',
      header: 'No',
      cell: ({ row }) => row.index + 1 + currentPage * pageSize,
      size: 96,
      meta: { selection: true },
    },
    {
      accessorKey: 'orderStatus',
      header: '주문 상태',
      size: 100,
      cell: ({ row }) => {
        const value = row.original.orderStatus;
        if (!value) return '-';

        return <StatusBadge value={value} colors={DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR} />;
      },
    },
    {
      accessorKey: 'statusUpdatedAt',
      header: '상태 마지막 업데이트 일시',
      size: 170,
      cell: ({ row }) => formatDate(row.original.statusUpdatedAt, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      accessorKey: 'clientOrderCode',
      header: '판매처 주문번호',
      size: 160,
    },
    {
      id: 'viewDetail',
      header: '',
      minSize: 80,
      size: 80,
      cell: ({ row }) => (
        <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original)}>
          상세보기
        </TextButtonCell>
      ),
    },
    {
      accessorKey: 'outboundCode',
      header: '출고번호',
      size: 160,
    },
    {
      accessorKey: 'estimatedDeliveryDate',
      header: '배송 예정일',
      size: 100,
      cell: ({ row }) => formatDate(row.original.estimatedDeliveryDate, DATE_FORMAT.DATE) ?? '-',
    },
    {
      id: 'deliveryType',
      accessorFn: (row) => row.deliveryType?.description,
      header: '배송 유형',
      size: 80,
    },
    {
      id: 'courierCode',
      accessorFn: (row) => row.courierCode?.description,
      header: '배송사',
      size: 88,
    },
    {
      accessorKey: 'receiverName',
      header: '수령자명',
      size: 100,
      cell: ({ row }) => maskName(row.original.receiverName) ?? '-',
    },
    {
      accessorKey: 'ordererName',
      header: '주문자명',
      size: 100,
      cell: ({ row }) => maskName(row.original.ordererName) ?? '-',
    },
    {
      accessorKey: 'productName',
      header: '상품명',
      minSize: 252,
      meta: { flex: true },
    },
    {
      accessorKey: 'createdAt',
      header: '주문 등록 일시',
      size: 147,
      cell: ({ row }) => formatDate(row.original.createdAt, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyOrdersColumnDefs;
